import { useState } from "react";
import { Sparkles, ArrowRight, Check, RefreshCcw, Loader2 } from "lucide-react";
import axios from "axios";
import { QUIZ_QUESTIONS, PROFILES, resolveProfile } from "../data/quiz";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const client = axios.create({ baseURL: API, validateStatus: () => true, withCredentials: true });
const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

function OptionButton({ opt, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`text-left border p-4 md:p-5 transition-all duration-300 ${
        active
          ? "border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/5"
          : "border-[hsl(var(--line))] hover:border-[hsl(var(--ink))]"
      }`}
    >
      <div className="font-display text-lg md:text-xl">{opt.label}</div>
      <div className="text-xs text-[hsl(var(--ink-soft))] mt-1">{opt.desc}</div>
    </button>
  );
}

export default function StyleQuiz({ topOfPage = false }) {
  const [step, setStep] = useState(0);           // 0..QUIZ_QUESTIONS.length-1, then "result"
  const [answers, setAnswers] = useState({});
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const totalSteps = QUIZ_QUESTIONS.length;
  const isResult = step === "result";
  const currentQuestion = !isResult ? QUIZ_QUESTIONS[step] : null;
  const profileId = isResult ? resolveProfile(answers) : null;
  const profile = profileId ? PROFILES[profileId] : null;

  const answer = (qId, optId) => {
    const next = { ...answers, [qId]: optId };
    setAnswers(next);
    if (step + 1 < totalSteps) {
      setStep(step + 1);
    } else {
      setStep("result");
    }
  };

  const restart = () => {
    setStep(0);
    setAnswers({});
    setName("");
    setEmail("");
    setSubmitted(false);
    setError("");
  };

  const canSubmit = name.trim().length >= 2 && isValidEmail(email.trim()) && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError("");
    const payloadAnswers = QUIZ_QUESTIONS.map((q) => ({
      question: q.prompt,
      choice: q.options.find((o) => o.id === answers[q.id])?.label || answers[q.id],
    }));
    const res = await client.post("/quiz/complete", {
      name: name.trim(),
      email: email.trim(),
      profile_id: profileId,
      profile_name: profile.name,
      answers: payloadAnswers,
    });
    setBusy(false);
    if (res.status >= 400) {
      setError(res.data?.detail || "Could not send. Please try again.");
      return;
    }
    setSubmitted(true);
  };

  return (
    <section
      className={`${topOfPage ? "pt-32 md:pt-36 pb-16 md:pb-20" : "py-24 md:py-32"} bg-[hsl(var(--bg-alt))]`}
      data-testid="style-quiz-section"
    >
      <div className="mx-auto max-w-[1200px] px-6 md:px-10">
        <div className="max-w-2xl">
          <div className="kicker text-[hsl(var(--accent))]">
            <Sparkles size={12} className="inline mr-2" />
            60-second Style Quiz
          </div>
          <h2 className="font-display text-3xl md:text-5xl mt-4 leading-tight">
            Discover your <span className="italic">celebration style</span>.
          </h2>
          <p className="mt-4 text-[hsl(var(--ink-soft))] text-sm md:text-base leading-relaxed">
            Three quick questions. Shubhra will send you a personalised lookbook of real
            weddings that match your aesthetic — no consultation call required.
          </p>
        </div>

        <div className="mt-12 bg-[hsl(var(--bg))] border border-[hsl(var(--line))] p-6 md:p-10">
          {/* Progress bar */}
          {!isResult && (
            <div className="flex items-center justify-between mb-8">
              <div className="kicker">
                Question {step + 1} / {totalSteps}
              </div>
              <div className="flex gap-2">
                {QUIZ_QUESTIONS.map((q, i) => (
                  <div
                    key={q.id}
                    className={`h-[2px] w-8 ${
                      i <= step ? "bg-[hsl(var(--accent))]" : "bg-[hsl(var(--line))]"
                    }`}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Question screen */}
          {!isResult && currentQuestion && (
            <div>
              <h3 className="font-display text-2xl md:text-3xl leading-snug">
                {currentQuestion.prompt}
              </h3>
              <div className="mt-6 grid sm:grid-cols-2 gap-3 md:gap-4">
                {currentQuestion.options.map((opt) => (
                  <OptionButton
                    key={opt.id}
                    opt={opt}
                    active={answers[currentQuestion.id] === opt.id}
                    onClick={() => answer(currentQuestion.id, opt.id)}
                    testid={`quiz-opt-${currentQuestion.id}-${opt.id}`}
                  />
                ))}
              </div>
              {step > 0 && (
                <button
                  onClick={() => setStep(step - 1)}
                  className="mt-6 text-[10px] tracking-[0.3em] uppercase text-[hsl(var(--ink-soft))] hover:text-[hsl(var(--accent))]"
                  data-testid="quiz-back"
                >
                  ← Back
                </button>
              )}
            </div>
          )}

          {/* Result screen */}
          {isResult && profile && (
            <div data-testid="quiz-result">
              <div className="kicker text-[hsl(var(--accent))]">Your style is</div>
              <h3 className="font-display text-4xl md:text-6xl mt-4 leading-[1.05]">
                <span className="italic">{profile.name}</span>
              </h3>
              <p className="mt-2 text-[hsl(var(--ink-soft))] text-lg italic">{profile.tagline}</p>

              <p className="mt-6 max-w-2xl text-[hsl(var(--ink-soft))] leading-relaxed">
                {profile.description}
              </p>

              <div className="mt-4 flex flex-wrap gap-2">
                {profile.keywords.map((k) => (
                  <span
                    key={k}
                    className="text-[10px] tracking-[0.25em] uppercase border border-[hsl(var(--line))] px-3 py-1"
                  >
                    {k}
                  </span>
                ))}
              </div>

              <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="quiz-result-images">
                {profile.images.map((src, i) => (
                  <div key={`${src}-${i}`} className="aspect-square overflow-hidden">
                    <img
                      src={src}
                      alt={`${profile.name} inspiration ${i + 1}`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                ))}
              </div>

              {/* Email capture */}
              {!submitted && (
                <div className="mt-10 border-t border-[hsl(var(--line))] pt-8">
                  <div className="kicker">Unlock your personal lookbook</div>
                  <p className="mt-3 text-sm text-[hsl(var(--ink-soft))] max-w-xl">
                    Drop your details — Shubhra will personally curate a <strong>20-image
                    lookbook</strong> of real {profile.name} celebrations and email it to you
                    within 24 hours.
                  </p>
                  <div className="mt-4 grid md:grid-cols-2 gap-3 max-w-xl">
                    <input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Your name"
                      data-testid="quiz-name-input"
                      className="bg-transparent border border-[hsl(var(--line))] p-3 text-sm outline-none focus:border-[hsl(var(--ink))]"
                    />
                    <input
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Your email"
                      type="email"
                      data-testid="quiz-email-input"
                      className="bg-transparent border border-[hsl(var(--line))] p-3 text-sm outline-none focus:border-[hsl(var(--ink))]"
                    />
                  </div>
                  {error && (
                    <div className="mt-3 text-xs text-[hsl(var(--accent))]">{error}</div>
                  )}
                  <div className="mt-5 flex flex-wrap items-center gap-4">
                    <button
                      onClick={submit}
                      disabled={!canSubmit}
                      data-testid="quiz-submit"
                      className="inline-flex items-center gap-2 bg-[hsl(var(--ink))] text-[hsl(var(--bg))] px-6 py-3 text-[11px] tracking-[0.3em] uppercase hover:bg-[hsl(var(--accent))] transition-colors duration-500 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {busy ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
                      Send my lookbook
                    </button>
                    <button
                      onClick={restart}
                      data-testid="quiz-restart"
                      className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[hsl(var(--ink-soft))] hover:text-[hsl(var(--accent))]"
                    >
                      <RefreshCcw size={12} /> Retake quiz
                    </button>
                  </div>
                </div>
              )}

              {submitted && (
                <div
                  className="mt-10 border-t border-[hsl(var(--line))] pt-8 text-center"
                  data-testid="quiz-submitted"
                >
                  <Check size={32} className="mx-auto text-[hsl(var(--accent))] mb-3" />
                  <div className="font-display text-2xl">Beautifully done.</div>
                  <p className="mt-2 text-sm text-[hsl(var(--ink-soft))] max-w-md mx-auto">
                    Your {profile.name} lookbook is on its way. Watch{" "}
                    <strong className="text-[hsl(var(--ink))]">{email}</strong> for a personal
                    note from Shubhra within 24 hours.
                  </p>
                  <button
                    onClick={restart}
                    className="mt-6 inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[hsl(var(--ink-soft))] hover:text-[hsl(var(--accent))]"
                  >
                    <RefreshCcw size={12} /> Try again with different answers
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
